import { useState } from "react";
import SectionBadge from "../ui/SectionBadge";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowDown01Icon,
  HelpCircleIcon,
  Clock01Icon,
  ShieldEnergyIcon,
  Calendar03Icon,
  ZapIcon,
  Message01Icon,
  CallIcon,
} from "hugeicons-react";

const faqs = [
  {
    icon: <Clock01Icon size={24} />,
    question: "كم تستغرق عملية التلميع التفصيلي؟",
    answer:
      "تعتمد المدة على حجم السيارة وحالتها، ولكن عادة ما تستغرق بين 3 إلى 5 ساعات لضمان أفضل نتيجة، حيث نولي اهتماماً لكل التفاصيل الدقيقة.",
  },
  {
    icon: <ShieldEnergyIcon size={24} />,
    question: "هل تستخدمون مواد أصلية وآمنة؟",
    answer:
      "نعم، نستخدم فقط مواد عالمية (مثل Koch Chemie و STEK) معتمدة لضمان الجودة وعدم الإضرار بطلاء أو داخلية السيارة. جميع منتجاتنا صديقة للبيئة.",
  },
  {
    icon: <HelpCircleIcon size={24} />,
    question: "هل يوجد ضمان على خدمات الحماية؟",
    answer:
      "بالتأكيد، نقدم ضماناً يصل إلى 5 سنوات على خدمات النانو سيراميك ضد الاصفرار والتقشر.",
  },
  {
    icon: <Calendar03Icon size={24} />,
    question: "كيف يمكنني حجز موعد؟",
    answer:
      "يمكنك الحجز بسهولة عبر نظام الحجز الذكي في موقعنا. اختر الخدمة، حدد الوقت المناسب، واستلم تأكيد الحجز فوراً.",
  },
  {
    icon: <ZapIcon size={24} />,
    question: "هل يمكنني الدفع عند الاستلام؟",
    answer:
      "نعم، نوفر خيارات دفع متعددة بما في ذلك الدفع نقداً أو عبر البطاقات البنكية عند الانتهاء من الخدمة واستلام سيارتك.",
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // WhatsApp number - format: country code + number without leading zero
  const whatsappNumber = "218930303046"; // Libya: 218, then 930303046
  const whatsappUrl = `https://wa.me/${whatsappNumber}`;

  return (
    <section
      className="py-24 md:py-32 bg-[#050000] relative overflow-hidden"
      id="faq"
    >
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-[0.02]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

      {/* Top Gradient Mask */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-[#050000] to-transparent z-10 pointer-events-none" />
      {/* Bottom Gradient Mask */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#050000] to-transparent z-10 pointer-events-none" />

      <div className="container mx-auto px-4 max-w-5xl relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <SectionBadge text="قاعدة المعلومات" icon={HelpCircleIcon} />

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 text-white">
            الأسئلة{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-red-600">
              الشائعة
            </span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg leading-relaxed">
            إجابات سريعة وشفافة لكل ما يتعلق بخدماتنا. لا تتردد في التواصل معنا
            لأي استفسار إضافي.
          </p>
        </motion.div>

        {/* FAQ Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-16">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              faq={faq}
              index={index}
              isOpen={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            />
          ))}
        </div>

        {/* Enhanced Contact Section */}
        <motion.div
          className="relative"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-[#0A0000] via-[#050000] to-[#0A0000] p-8 md:p-12 overflow-hidden">
            {/* Background Glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5 opacity-50" />
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-primary/10 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />

            <div className="relative z-10 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/20 border border-primary/30 mb-6">
                <HelpCircleIcon size={32} className="text-primary" />
              </div>

              <h3 className="text-2xl md:text-3xl font-black text-white mb-3">
                لم تجد إجابة لسؤالك؟
              </h3>
              <p className="text-gray-400 mb-8 max-w-md mx-auto">
                فريقنا جاهز للإجابة على جميع استفساراتك. تواصل معنا الآن عبر أي
                من القنوات التالية.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="#location"
                  className="group inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-white/5 border border-white/10 text-white hover:bg-primary hover:border-primary hover:shadow-[0_0_30px_rgba(220,20,60,0.4)] transition-all duration-300 font-medium min-w-[200px] justify-center"
                >
                  <CallIcon size={20} />
                  <span>تواصل معنا مباشرة</span>
                </a>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="group inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-[#25D366]/10 border border-[#25D366]/30 text-[#25D366] hover:bg-[#25D366] hover:text-white hover:shadow-[0_0_30px_rgba(37,211,102,0.4)] transition-all duration-300 font-medium min-w-[200px] justify-center"
                >
                  <Message01Icon size={20} />
                  <span>واتساب</span>
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function FAQItem({
  faq,
  index,
  isOpen,
  onClick,
}: {
  faq: (typeof faqs)[0];
  index: number;
  isOpen: boolean;
  onClick: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="group"
    >
      <div
        className={`
          relative rounded-2xl border transition-all duration-300 overflow-hidden
          ${
            isOpen
              ? "bg-gradient-to-br from-[#0A0000] to-[#050000] border-primary/40 shadow-[0_0_30px_rgba(220,20,60,0.2)]"
              : "bg-white/[0.02] border-white/5 hover:border-primary/30 hover:bg-white/[0.04] hover:shadow-[0_0_20px_rgba(220,20,60,0.1)]"
          }
        `}
      >
        {/* Glow effect when open */}
        {isOpen && (
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />
        )}

        <button
          onClick={onClick}
          className="w-full flex items-center gap-4 p-6 text-right transition-colors relative z-10"
        >
          {/* Icon */}
          <div
            className={`
              w-14 h-14 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300
              ${
                isOpen
                  ? "bg-primary text-white scale-110 shadow-[0_0_20px_rgba(220,20,60,0.4)]"
                  : "bg-white/5 text-gray-400 group-hover:text-primary group-hover:bg-primary/10"
              }
            `}
          >
            {faq.icon}
          </div>

          {/* Question */}
          <span
            className={`
              font-bold text-base md:text-lg flex-1 transition-colors text-right
              ${isOpen ? "text-white" : "text-gray-300 group-hover:text-white"}
            `}
          >
            {faq.question}
          </span>

          {/* Arrow */}
          <div
            className={`
              w-10 h-10 rounded-lg flex items-center justify-center shrink-0 transition-all duration-300
              ${
                isOpen
                  ? "rotate-180 text-primary bg-primary/10"
                  : "text-gray-500 group-hover:text-gray-300 group-hover:bg-white/5"
              }
            `}
          >
            <ArrowDown01Icon size={20} />
          </div>
        </button>

        {/* Answer */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="relative z-10"
            >
              <div className="px-6 pb-6 pr-[88px]">
                <div className="h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent mb-4" />
                <p className="text-gray-400 leading-relaxed text-sm md:text-base">
                  {faq.answer}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
